import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import LegalLayout from '../LegalLayout';

const PrivacyPolicy = () => {
  const accentColor = '#10b981';
  const lastUpdated = 'February 16, 2026';

  return (
    <LegalLayout app="TapLedger" docType="Privacy Policy" icon={Shield} lastUpdated={lastUpdated}>
          <div className="space-y-8">
            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Introduction
              </h2>
              <p className="mb-4 leading-relaxed">
                Feral Techlab ("we," "our," or "us") operates the TapLedger mobile application
                (the "App"). TapLedger is a fast, lightweight expense tracking app designed to help
                you log and understand your spending habits.
              </p>
              <p className="leading-relaxed">
                This Privacy Policy explains how we handle your information when you use our App.
                We are committed to protecting your privacy and being transparent about our practices.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Our Privacy Commitment
              </h2>
              <p className="mb-4 leading-relaxed">
                TapLedger is built with privacy as a core principle:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>100% Offline:</strong> All your expense data is stored locally on your device. No data is sent to external servers.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No User Accounts:</strong> The App does not require registration, login, or any form of account creation.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Analytics:</strong> We do not collect usage statistics, behavioral data, or any form of telemetry.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Third-Party SDKs:</strong> We use only Apple's native frameworks. No analytics, tracking, or advertising SDKs are included.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Information We Store
              </h2>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Expense Data
              </h3>
              <p className="mb-4 leading-relaxed">
                TapLedger stores the expense records you create, which include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Expense amount</li>
                <li>Category name and emoji</li>
                <li>Payment type (Cash, UPI, Bank, or Card)</li>
                <li>Optional notes you add to transactions</li>
                <li>Date and time of the expense</li>
              </ul>
              <p className="leading-relaxed">
                <strong style={{ color: 'var(--text-primary)' }}>Important:</strong> All expense
                data is stored locally on your device using Apple's SwiftData framework and is never
                transmitted to any server.
              </p>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                User Preferences
              </h3>
              <p className="mb-4 leading-relaxed">
                The App stores your preferences locally, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Preferred currency (e.g., INR, USD, EUR)</li>
                <li>Default payment type</li>
                <li>Onboarding completion status</li>
                <li>Security settings (whether app lock is enabled)</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 mt-6" style={{ color: 'var(--text-primary)' }}>
                Security Data
              </h3>
              <p className="leading-relaxed">
                If you enable App Lock (Pro feature), a 4-digit backup PIN is stored locally on your
                device. Biometric authentication (Face ID, Touch ID, or Optic ID) is handled entirely
                by iOS and we never access or store your biometric data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Storage & Security
              </h2>
              <p className="mb-4 leading-relaxed">
                Your data is protected by multiple layers of security:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>Local Storage:</strong> All data is stored in a SwiftData database on your device, protected by iOS's built-in encryption.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Biometric Protection:</strong> Access to the App can be protected with Face ID, Touch ID, or Optic ID (Pro feature).</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>PIN Protection:</strong> A 4-digit backup PIN provides an additional layer of security (Pro feature).</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>No Network Access:</strong> The App makes no network connections whatsoever, eliminating the risk of remote data breaches.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                What We Do NOT Collect
              </h2>
              <p className="mb-4 leading-relaxed">
                TapLedger does not collect, store, or transmit:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Personal identification information</li>
                <li>Location data</li>
                <li>Contact information</li>
                <li>Financial account details (bank accounts, card numbers, UPI IDs)</li>
                <li>Photos or media files</li>
                <li>Browsing history</li>
                <li>Device identifiers for tracking purposes</li>
                <li>Usage analytics or behavioral data</li>
                <li>Advertising data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                In-App Purchases
              </h2>
              <p className="leading-relaxed">
                TapLedger offers optional Pro features through a one-time in-app purchase processed
                entirely by the Apple App Store. We do not process, store, or have access to your
                payment information. Purchase verification is handled through Apple's StoreKit framework.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Third-Party Services
              </h2>
              <p className="leading-relaxed">
                TapLedger uses <strong style={{ color: 'var(--text-primary)' }}>only Apple's native frameworks</strong>:
                SwiftUI, SwiftData, StoreKit, and LocalAuthentication. We do not integrate any
                third-party SDKs, analytics tools, crash reporters, or advertising networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Data Export
              </h2>
              <p className="leading-relaxed">
                TapLedger Pro allows you to export your expense data as a CSV file. This export is
                generated locally on your device. When you share the exported file, it is transmitted
                through your chosen sharing method (e.g., AirDrop, email, messaging) and is subject
                to the privacy practices of those services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Your Rights & Data Control
              </h2>
              <p className="mb-4 leading-relaxed">
                You have complete control over your data:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong style={{ color: 'var(--text-primary)' }}>View:</strong> Access all your expense records and analytics within the App.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Delete:</strong> Remove individual expenses or all data at any time.</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Export:</strong> Export your data as a CSV file (Pro feature).</li>
                <li><strong style={{ color: 'var(--text-primary)' }}>Uninstall:</strong> Removing the App deletes all locally stored data permanently.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Children's Privacy
              </h2>
              <p className="leading-relaxed">
                TapLedger is not directed at children under the age of 13. We do not knowingly
                collect personal information from children. If you believe a child has provided
                us with personal information, please contact us so we can take appropriate action.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Changes to This Policy
              </h2>
              <p className="leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any
                changes by posting the new Privacy Policy on this page and updating the "Last
                updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                Contact Us
              </h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices,
                please contact us:
              </p>
              <div className="mt-4 space-y-2">
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                  <a
                    href="mailto:support@feraltechlabs.co.in"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    support@feraltechlabs.co.in
                  </a>
                </p>
                <p>
                  <strong style={{ color: 'var(--text-primary)' }}>Support:</strong>{' '}
                  <Link
                    to="/apps/tapledger/support"
                    className="transition-colors"
                    style={{ color: accentColor }}
                  >
                    TapLedger Support Page
                  </Link>
                </p>
              </div>
            </section>
          </div>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
